import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent, type ReactNode } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import "./index.css";
import { embedSnippet } from "@/lib/embed-snippet";
import { classifyIpChain, extractIpv4FromChain, type ClassifiedIp } from "@/ip-utils";
import { VisitorMap, type MapLocation } from "@/components/VisitorMap";
import { UtmSourceStats } from "@/components/UtmSourceStats";

type LiveFeedRow = {
  domain: string;
  name: string | null;
  industry: string | null;
  size: string | null;
  country: string | null;
  last_url: string | null;
  last_seen: number;
  confidence: number | null;
  ip: string | null;
  // Geolocation data (ip-api.com)
  geo_country: string | null;
  geo_country_code: string | null;
  geo_continent: string | null;
  geo_continent_code: string | null;
  geo_region: string | null;
  geo_region_name: string | null;
  geo_city: string | null;
  geo_zip: string | null;
  geo_district: string | null;
  geo_lat: number | null;
  geo_lon: number | null;
  geo_timezone: string | null;
  geo_timezone_offset: number | null;
  geo_currency: string | null;
  geo_isp: string | null;
  geo_org: string | null;
  geo_as: string | null;
  geo_asname: string | null;
  geo_is_mobile: number | null;
  geo_is_vpn: number | null;
  geo_is_datacenter: number | null;
  geo_reverse_dns: string | null;
  geo_fetched_at: number | null;
  geo_api_status: string | null;
  // Company enrichment (IPinfo IP-to-Company)
  company_name: string | null;
  company_domain: string | null;
  company_type: string | null;
  enrichment_confidence: number | null;
  // IPv4 Diagnostics
  diag_ipv4_resolved: number | null;
  diag_ipv4_source: string | null;
  diag_ipv4_error: string | null;
  diag_ipv4_duration_ms: number | null;
  // Device/Browser/OS Info
  browser: string | null;
  browser_version: string | null;
  os: string | null;
  os_version: string | null;
  device_type: string | null;
  device_vendor: string | null;
  device_model: string | null;
};

type DnsCacheStats = {
  cacheHitsCompleted: number;
  cacheHitsInflight: number;
  cacheMisses: number;
  size: number;
  errors: number;
  totalCount: number;
};

type Diagnostics = {
  timestamp: number;
  bun: {
    version: string;
    revision?: string | null;
    runtime?: string | null;
  };
  environment: string;
  server: {
    url: string;
    hostname: string;
    port: number;
  };
  database: {
    path: string;
  };
  dnsCache?: DnsCacheStats | null;
  dnsPrefetch?: {
    hostname: string;
    port: number;
    supported: boolean;
    attempted: boolean;
    error?: string;
  } | null;
  site: {
    id: string;
    url: string;
  };
  event: {
    id: string;
    type: string;
    received_at: number;
  };
  client: {
    ip?: string | null;
    ipv4?: string | null;
    ipChain?: string[] | null;
    userAgent?: string | null;
  };
};

type LiveEventMeta = {
  message?: string;
  diagnostics?: Diagnostics;
  // Pixel diagnostics
  ipv4?: string;
  ipv4_diagnostics?: {
    ipv4_resolved?: number;
    ipv4_source?: string;
    ipv4_error?: string | null;
    ipv4_duration_ms?: number;
  };
  [key: string]: unknown;
};

type LiveStreamEvent = {
  id: string;
  type: string;
  received_at: number;
  url: string;
  cid: string;
  ip: string;
  meta?: LiveEventMeta;
};

type LiveStreamPayload = {
  rows: LiveFeedRow[];
  timestamp: number;
  total: number;
  event?: LiveStreamEvent;
};

type FetchState = "idle" | "loading" | "error";
type LiveStatus = "idle" | "connecting" | "connected" | "error";

type SiteResponse = {
  site_id: string;
  url: string;
  created_at: number;
};

const LIVE_STATUS_LABELS: Record<LiveStatus, string> = {
  idle: "Idle",
  connecting: "Connecting…",
  connected: "Live",
  error: "Reconnecting…",
};

const LIVE_STATUS_STYLES: Record<LiveStatus, string> = {
  idle: "border-slate-200 text-slate-400",
  connecting: "border-sky-300 text-sky-600",
  connected: "border-emerald-500 text-emerald-600",
  error: "border-red-400 text-red-600",
};

export function App() {
  const [site, setSite] = useState<SiteResponse | null>(null);
  const [inputUrl, setInputUrl] = useState("");
  const [customSiteId, setCustomSiteId] = useState("");
  const [creatingSite, setCreatingSite] = useState(false);
  const [siteError, setSiteError] = useState<string | null>(null);
  const [rows, setRows] = useState<LiveFeedRow[]>([]);
  const [total, setTotal] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize] = useState<number>(50);
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [state, setState] = useState<FetchState>("idle");
  const [error, setError] = useState<string | null>(null);
  const [copyState, setCopyState] = useState<"idle" | "copied" | "error">("idle");
  const [liveStatus, setLiveStatus] = useState<LiveStatus>("idle");
  const [handshakeMessage, setHandshakeMessage] = useState<string | null>(null);
  const [diagnostics, setDiagnostics] = useState<Diagnostics | null>(null);
  const [pixelMeta, setPixelMeta] = useState<LiveEventMeta | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [clearingSite, setClearingSite] = useState(false);
  const [loaderType, setLoaderType] = useState<"sync" | "async">("async");
  const [mapLocations, setMapLocations] = useState<MapLocation[]>([]);
  const [newVisitorIps, setNewVisitorIps] = useState<Set<string>>(new Set());
  const [allTimeStats, setAllTimeStats] = useState<{
    total_visitors: number;
    business_visitors: number;
    unique_countries: number;
    unique_utm_sources: number;
    chatgpt_referrals: number;
    most_frequent_visitor: string | null;
    most_frequent_visitor_count: number;
  } | null>(null);
  const lastUpdated = useRef<number | null>(null);
  const eventSourceRef = useRef<EventSource | null>(null);
  const reconnectTimer = useRef<number | null>(null);
  const currentPageRef = useRef<number>(currentPage);

  // Sync currentPage to ref so SSE can read latest value without reconnecting
  useEffect(() => {
    currentPageRef.current = currentPage;
  }, [currentPage]);

  const fetchFeed = useCallback(
    async (targetSiteId?: string, opts: { silent?: boolean; page?: number } = {}) => {
      const siteId = targetSiteId ?? site?.site_id;
      if (!siteId) return;
      if (!opts.silent) setState("loading");
      const page = opts.page ?? 1;
      const offset = (page - 1) * pageSize;
      try {
        const res = await fetch(`/api/live-feed?site_id=${encodeURIComponent(siteId)}&limit=${pageSize}&offset=${offset}`, {
          headers: {
            "Accept": "application/json",
          },
        });
        if (!res.ok) {
          throw new Error(`Request failed with ${res.status}`);
        }
        const data = await res.json() as { rows: LiveFeedRow[]; total: number; limit: number; offset: number };
        setRows(data.rows);
        setTotal(data.total);
        lastUpdated.current = Date.now();
        setError(null);
        setState("idle");
      } catch (err) {
        const message = err instanceof Error ? err.message : "unknown error";
        setError(message);
        if (!opts.silent) {
          setState("error");
        }
      }
    },
    [site?.site_id, pageSize]
  );

  const fetchMapData = useCallback(
    async (targetSiteId?: string) => {
      const siteId = targetSiteId ?? site?.site_id;
      if (!siteId) return;
      try {
        const res = await fetch(`/api/map-data?site_id=${encodeURIComponent(siteId)}`, {
          headers: {
            "Accept": "application/json",
          },
        });
        if (!res.ok) {
          throw new Error(`Map data request failed with ${res.status}`);
        }
        const data = await res.json();
        setMapLocations(data.locations || []);
      } catch (err) {
        console.error('Failed to fetch map data:', err);
        setMapLocations([]);
      }
    },
    [site?.site_id]
  );

  const fetchAllTimeStats = useCallback(
    async (targetSiteId?: string) => {
      const siteId = targetSiteId ?? site?.site_id;
      if (!siteId) return;
      try {
        const res = await fetch(`/api/all-time-stats?site_id=${encodeURIComponent(siteId)}`, {
          headers: {
            "Accept": "application/json",
          },
        });
        if (!res.ok) {
          throw new Error(`All-time stats request failed with ${res.status}`);
        }
        const data = await res.json();
        setAllTimeStats(data.stats || null);
      } catch (err) {
        console.error('Failed to fetch all-time stats:', err);
        setAllTimeStats(null);
      }
    },
    [site?.site_id]
  );

  // Fetch map data and all-time stats when site changes or every 30 seconds
  useEffect(() => {
    if (!site) return;

    fetchMapData(site.site_id);
    fetchAllTimeStats(site.site_id);
    const interval = setInterval(() => {
      fetchMapData(site.site_id);
      fetchAllTimeStats(site.site_id);
    }, 30000);

    return () => clearInterval(interval);
  }, [site, fetchMapData, fetchAllTimeStats]);

  useEffect(() => {
    if (typeof window === "undefined" || typeof EventSource === "undefined") {
      return;
    }

    const cleanupTimersAndStreams = () => {
      if (reconnectTimer.current) {
        window.clearTimeout(reconnectTimer.current);
        reconnectTimer.current = null;
      }
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
    };

    if (!site?.site_id) {
      cleanupTimersAndStreams();
      setLiveStatus("idle");
      setHandshakeMessage(null);
      setRows([]);
      lastUpdated.current = null;
      return;
    }

    let stopped = false;

    const scheduleReconnect = () => {
      if (stopped) return;
      if (reconnectTimer.current) {
        window.clearTimeout(reconnectTimer.current);
      }
      reconnectTimer.current = window.setTimeout(() => {
        reconnectTimer.current = null;
        connect();
      }, 5000);
    };

    const connect = () => {
      if (stopped) return;
      cleanupTimersAndStreams();
      setLiveStatus("connecting");

      // Initial fetch - only update rows if user is viewing page 1
      // If user is on a historical page, skip updating to preserve their view
      const initialFetch = async () => {
        // Only fetch if viewing page 1, otherwise preserve current page view
        if (currentPageRef.current !== 1) {
          return;
        }

        try {
          setState("loading");
          const res = await fetch(`/api/live-feed?site_id=${encodeURIComponent(site.site_id)}&limit=${pageSize}&offset=0`, {
            headers: { "Accept": "application/json" },
          });
          if (res.ok) {
            const data = await res.json() as { rows: LiveFeedRow[]; total: number; limit: number; offset: number };
            setRows(data.rows);
            setTotal(data.total);
            lastUpdated.current = Date.now();
            setError(null);
            setState("idle");
          }
        } catch (err) {
          console.error("Initial feed fetch failed:", err);
        }
      };
      void initialFetch();

      const source = new EventSource(`/api/live-feed/stream?site_id=${encodeURIComponent(site.site_id)}&limit=${pageSize}`);
      eventSourceRef.current = source;

      source.onopen = () => {
        if (stopped) return;
        setLiveStatus("connected");
      };

      source.onmessage = event => {
        if (stopped) return;
        try {
          const payload = JSON.parse(event.data) as LiveStreamPayload;

          // Only update rows if user is viewing page 1 (most recent entries)
          // Skip updates when viewing historical pages to preserve pagination state
          if (currentPageRef.current === 1) {
            setRows(payload.rows);
            lastUpdated.current = payload.timestamp;
            setError(null);
            setState("idle");
          }

          // Always update total count from the SSE payload
          // This ensures pagination controls show accurate counts even on other pages
          setTotal(payload.total);

          // Refresh map data on new events
          void fetchMapData(site.site_id);

          // Track new visitor IPs for pulse effect
          if (payload.event?.ip) {
            setNewVisitorIps(prev => {
              const next = new Set(prev);
              next.add(payload.event!.ip);
              // Remove after 5 seconds
              setTimeout(() => {
                setNewVisitorIps(current => {
                  const updated = new Set(current);
                  updated.delete(payload.event!.ip);
                  return updated;
                });
              }, 5000);
              return next;
            });
          }

          // Check for diagnostics by presence of ipv4_diagnostics in page_view events
          const meta = payload.event?.meta;
          const hasRichDiagnostics = meta && payload.event?.type === "page_view" && (
            meta.ipv4_diagnostics
          );

          if (hasRichDiagnostics) {
            const message =
              meta && typeof meta.message === "string" ? meta.message : "Pixel diagnostics received.";
            setHandshakeMessage(message);
            if (meta && isDiagnostics(meta.diagnostics)) {
              setDiagnostics(meta.diagnostics);
            }
            if (meta) {
              setPixelMeta(meta);
            }
          }
        } catch (err) {
          console.error("[acctrev] failed to parse live feed event", err);
        }
      };

      source.onerror = (event) => {
        if (stopped) return;
        console.error("[acctrev] EventSource error", {
          readyState: source.readyState,
          url: source.url,
          event,
        });
        setLiveStatus("error");
        cleanupTimersAndStreams();
        scheduleReconnect();
      };
    };

    connect();

    return () => {
      stopped = true;
      cleanupTimersAndStreams();
    };
  }, [site?.site_id, pageSize, fetchMapData]);

  useEffect(() => {
    if (site?.site_id) {
      setHandshakeMessage(null);
      setDiagnostics(null);
      setPixelMeta(null);
      setDeleteError(null);
      setClearingSite(false);
    }
  }, [site?.site_id]);

  const snippet = useMemo(() => (site ? embedSnippet(site.site_id, { loaderType }) : ""), [site, loaderType]);
  const handleCreateSite = useCallback(
    (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      const nextUrl = inputUrl.trim();
      if (!nextUrl) {
        setSiteError("Enter the full URL for your website.");
        return;
      }
      setSiteError(null);
      setCreatingSite(true);
      const payload: Record<string, string> = { url: nextUrl };
      const trimmedCustomSiteId = customSiteId.trim();
      if (trimmedCustomSiteId) {
        payload.site_id = trimmedCustomSiteId;
      }
      fetch("/api/sites", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      })
        .then(async response => {
          if (!response.ok) {
            const data = (await response.json().catch(() => ({}))) as { error?: string };
            throw new Error(data.error || `Request failed (${response.status})`);
          }
          return response.json() as Promise<SiteResponse>;
        })
        .then(siteResponse => {
          setSite(siteResponse);
          setInputUrl(siteResponse.url);
          setCustomSiteId(siteResponse.site_id);
          setDeleteError(null);
          setCurrentPage(1); // Reset to page 1 when changing sites
          fetchFeed(siteResponse.site_id, { page: 1 });
        })
        .catch(err => {
          const message = err instanceof Error ? formatSiteError(err.message) : "Failed to generate site ID.";
          setSiteError(message);
        })
        .finally(() => setCreatingSite(false));
    },
    [customSiteId, fetchFeed, inputUrl]
  );

  const handleCopySnippet = useCallback(() => {
    if (!snippet) return;
    const write = async () => {
      try {
        await navigator.clipboard.writeText(snippet);
        setCopyState("copied");
        setTimeout(() => setCopyState("idle"), 2000);
      } catch {
        setCopyState("error");
        setTimeout(() => setCopyState("idle"), 3000);
      }
    };
    void write();
  }, [snippet]);

  const handleClearSiteData = useCallback(async () => {
    const siteId = site?.site_id;
    if (!siteId) return;

    if (typeof window !== "undefined") {
      const confirmed = window.confirm("Delete all company data for this site?");
      if (!confirmed) {
        return;
      }
    }

    setClearingSite(true);
    setDeleteError(null);

    try {
      const response = await fetch("/api/accounts", {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ site_id: siteId }),
      });
      if (!response.ok) {
        const data = (await response.json().catch(() => ({}))) as { error?: string };
        throw new Error(data.error || `Request failed (${response.status})`);
      }
      setRows([]);
      lastUpdated.current = Date.now();
    } catch (err) {
      const message = err instanceof Error ? formatDeleteError(err.message) : "Failed to delete company data.";
      setDeleteError(message);
    } finally {
      setClearingSite(false);
    }
  }, [site?.site_id]);

  const lastUpdatedLabel = lastUpdated.current
    ? new Intl.DateTimeFormat(undefined, {
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      }).format(lastUpdated.current)
    : "never";

  return (
    <div className="min-h-screen w-full bg-slate-100 text-slate-900">
      <header className="border-b border-slate-200 bg-white/90 backdrop-blur">
        <div className="mx-auto flex w-full max-w-5xl flex-col gap-4 px-6 py-8">
          <div>
            <p className="text-sm font-medium uppercase tracking-[0.2em] text-slate-500">AcctRev</p>
            <h1 className="text-3xl font-semibold text-slate-900">Live Feed</h1>
            <p className="text-sm text-slate-500">
              Company-level attribution powered by the collector, resolver, and SQLite cache.
            </p>
          </div>
          <Card className="border-slate-200">
            <CardHeader className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <CardTitle>Generate your site ID</CardTitle>
                <CardDescription>
                  Provide your website URL and we’ll create a site ID plus pixel snippet.
                </CardDescription>
              </div>
              <span className="text-xs uppercase tracking-widest text-slate-400">
                {site ? `Site ID ${site.site_id}` : "No site yet"}
              </span>
            </CardHeader>
            <CardContent>
              <form className="mx-auto flex w-full max-w-lg flex-col items-center gap-4 text-center" onSubmit={handleCreateSite}>
                <div className="flex w-full flex-col gap-1 text-left">
                  <Label htmlFor="site-url">
                    Website URL
                  </Label>
                  <Input
                    id="site-url"
                    value={inputUrl}
                    onChange={event => setInputUrl(event.target.value)}
                    placeholder="https://example.com"
                    className="text-center sm:text-left"
                  />
                </div>
                <div className="flex w-full flex-col gap-1 text-left">
                  <Label htmlFor="site-id">
                    Custom site ID (optional)
                  </Label>
                  <Input
                    id="site-id"
                    value={customSiteId}
                    onChange={event => setCustomSiteId(event.target.value)}
                    placeholder="SITE_DEBUG123"
                    className="text-center sm:text-left"
                  />
                  <p className="text-[11px] text-slate-500 text-center sm:text-left">
                    Leave blank to auto-generate. SITE_ is added automatically if missing.
                  </p>
                </div>
                <Button type="submit" variant="default" disabled={creatingSite}>
                  {creatingSite ? "Generating…" : "Generate site ID"}
                </Button>
              </form>
              {siteError && (
                <p role="alert" className="mt-2 text-sm text-red-500">
                  {siteError}
                </p>
              )}
              {site && (
                <div className="mt-4 rounded-md border border-slate-200 bg-slate-50 p-4 text-sm">
                  <div className="space-y-2">
                    <div>
                      <p className="text-slate-500">Site URL</p>
                      <p className="font-medium text-slate-900 break-all">{site.url}</p>
                    </div>
                    <div>
                      <p className="text-slate-500">Site ID</p>
                      <code className="inline-block rounded bg-white px-2 py-1 font-mono text-xs text-slate-900 shadow-sm">
                        {site.site_id}
                      </code>
                    </div>
                  </div>
                  <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                    <p className="text-xs text-slate-500">Need to reset test data for this site?</p>
                    <Button
                      type="button"
                      variant="destructive"
                      size="sm"
                      onClick={handleClearSiteData}
                      disabled={clearingSite}
                    >
                      {clearingSite ? "Deleting…" : "Purge site data"}
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </header>

      <main className="mx-auto flex w-full max-w-5xl flex-col gap-6 px-6 py-10">
        {/* Latest Visitor Debug Panel - Always Visible */}
        {site && rows.length > 0 && (() => {
          const latestVisitor = rows[0];
          if (!latestVisitor) return null;

          const ipParts = latestVisitor.ip
            ? latestVisitor.ip.split("|").map(part => part.trim()).filter(Boolean)
            : [];
          const classifiedIps = ipParts.length ? classifyIpChain(ipParts) : [];

          return (
            <Card>
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle className="text-lg">Latest Visitor Debug Info</CardTitle>
                    <CardDescription>
                      Real-time diagnostics for the most recent unique visitor
                    </CardDescription>
                  </div>
                  <div className="text-xs text-slate-400">
                    {new Date(latestVisitor.last_seen).toLocaleString()}
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                    {/* Company & IP Info */}
                    <div className="space-y-2">
                      <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">Visitor Identity</h3>
                      <div className="space-y-1.5 text-sm">
                        {latestVisitor.company_name || latestVisitor.geo_org ? (
                          <div>
                            <span className="text-slate-500">Company:</span>{" "}
                            <span className="font-medium">{latestVisitor.company_name || latestVisitor.geo_org}</span>
                            {latestVisitor.geo_org && !latestVisitor.company_name && (
                              <span className="ml-1 text-xs text-slate-400">(from network)</span>
                            )}
                          </div>
                        ) : (
                          <div>
                            <span className="text-slate-500">Company:</span>{" "}
                            <span className="text-slate-400">Unknown</span>
                          </div>
                        )}
                        {latestVisitor.company_domain && (
                          <div>
                            <span className="text-slate-500">Domain:</span>{" "}
                            <span className="font-medium">{latestVisitor.company_domain}</span>
                          </div>
                        )}
                        {latestVisitor.enrichment_confidence !== null && (
                          <div>
                            <span className="text-slate-500">Confidence:</span>{" "}
                            <span className="font-medium">{latestVisitor.enrichment_confidence}%</span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* IP Chain */}
                    {classifiedIps.length > 0 && (
                      <div className="space-y-2">
                        <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">IP Address Chain</h3>
                        <div className="space-y-1.5">
                          {classifiedIps.map((ip, idx) => (
                            <div key={idx} className="flex items-center gap-2 text-sm">
                              <span className="inline-block min-w-[45px] rounded bg-slate-200 px-2 py-0.5 text-[10px] font-semibold text-slate-600">
                                {ip.label}
                              </span>
                              <span className="font-mono text-slate-700">{ip.address}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Location */}
                    {(latestVisitor.geo_city || latestVisitor.geo_country) && (
                      <div className="space-y-2">
                        <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">Location</h3>
                        <div className="space-y-1 text-sm">
                          {latestVisitor.geo_city && latestVisitor.geo_country && (
                            <div>
                              <span className="font-medium">{latestVisitor.geo_city}, {latestVisitor.geo_country}</span>
                            </div>
                          )}
                          {latestVisitor.geo_isp && (
                            <div>
                              <span className="text-slate-500">ISP:</span>{" "}
                              <span className="text-slate-700">{latestVisitor.geo_isp}</span>
                            </div>
                          )}
                          {latestVisitor.geo_reverse_dns && (
                            <div>
                              <span className="text-slate-500">Reverse DNS:</span>{" "}
                              <span className="font-mono text-[11px] text-slate-700">{latestVisitor.geo_reverse_dns}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                </div>
              </CardContent>
            </Card>
          );
        })()}

        {site && (
          <>
            {/* Quick Stats Overview (All-Time) */}
            {site && allTimeStats && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            <div className="bg-white rounded-lg border border-slate-200 p-4">
              <div className="text-sm text-slate-500">Total Visitors</div>
              <div className="text-2xl font-bold text-slate-900 mt-1">{allTimeStats.total_visitors}</div>
              <div className="text-xs text-slate-400 mt-1">All-time</div>
            </div>
            <div className="bg-white rounded-lg border border-slate-200 p-4">
              <div className="text-sm text-slate-500">Business</div>
              <div className="text-2xl font-bold text-emerald-600 mt-1">
                {allTimeStats.business_visitors}
              </div>
              <div className="text-xs text-slate-400 mt-1">All-time</div>
            </div>
            <div className="bg-white rounded-lg border border-slate-200 p-4">
              <div className="text-sm text-slate-500">Countries</div>
              <div className="text-2xl font-bold text-blue-600 mt-1">
                {allTimeStats.unique_countries}
              </div>
              <div className="text-xs text-slate-400 mt-1">All-time</div>
            </div>
            <div className="bg-white rounded-lg border border-slate-200 p-4">
              <div className="text-sm text-slate-500">UTM Sources</div>
              <div className="text-2xl font-bold text-orange-600 mt-1">
                {allTimeStats.unique_utm_sources}
              </div>
              <div className="text-xs text-slate-400 mt-1">All-time</div>
            </div>
            <div className="bg-white rounded-lg border border-slate-200 p-4">
              <div className="text-sm text-slate-500">ChatGPT</div>
              <div className="text-2xl font-bold text-cyan-600 mt-1">
                {allTimeStats.chatgpt_referrals}
              </div>
              <div className="text-xs text-slate-400 mt-1">Visitors</div>
            </div>
            <div className="bg-white rounded-lg border border-slate-200 p-4">
              <div className="text-sm text-slate-500">Most Frequent</div>
              <div className="text-2xl font-bold text-purple-600 mt-1 truncate font-mono" title={allTimeStats.most_frequent_visitor || "None"}>
                {extractIpv4FromChain(allTimeStats.most_frequent_visitor) || allTimeStats.most_frequent_visitor || "—"}
              </div>
              <div className="text-xs text-slate-400 mt-1">
                {allTimeStats.most_frequent_visitor_count > 0 ? `${allTimeStats.most_frequent_visitor_count} visits` : "No visits yet"}
              </div>
            </div>
          </div>
        )}

        {/* UTM Source Statistics - Shows ChatGPT and other traffic sources */}
        {site && <UtmSourceStats siteId={site.site_id} />}

        <Card>
          <CardHeader className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <CardTitle>Company Visits (All Time)</CardTitle>
              <CardDescription>All visitor activity with company intelligence and device tracking.</CardDescription>
              <p className="mt-1 text-xs uppercase tracking-widest text-slate-400">
                Last updated {site ? lastUpdatedLabel : "—"}
              </p>
              {handshakeMessage && (
                <p className="mt-2 text-xs font-medium text-emerald-600">{handshakeMessage}</p>
              )}
              {diagnostics && <DiagnosticsPanel diagnostics={diagnostics} pixelMeta={pixelMeta} />}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <span
                className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-medium ${LIVE_STATUS_STYLES[liveStatus]}`}
              >
                <span className="h-2 w-2 rounded-full bg-current" aria-hidden />
                {LIVE_STATUS_LABELS[liveStatus]}
              </span>
            </div>
          </CardHeader>
          <CardContent className="overflow-x-auto">
            {site && deleteError && (
              <p role="alert" className="mb-3 text-sm text-red-500">
                {deleteError}
              </p>
            )}
            {!site ? (
              <p className="py-10 text-center text-sm text-slate-500">
                Generate a site ID to start capturing and viewing traffic.
              </p>
            ) : rows.length === 0 ? (
              <EmptyState state={state} error={error} />
            ) : (
              <table className="min-w-full text-left text-sm">
                <thead className="border-b border-slate-200 text-xs uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="py-3 pr-4 w-[60px]">Debug</th>
                    <th className="py-3 pr-4 min-w-[180px]">Company</th>
                    <th className="py-3 pr-4 min-w-[140px]">Domain</th>
                    <th className="py-3 pr-4 min-w-[120px]">UTM Source</th>
                    <th className="py-3 pr-4 min-w-[140px]">Device</th>
                    <th className="py-3 pr-4 min-w-[180px]">Location</th>
                    <th className="py-3 pr-4 min-w-[240px]">Last URL</th>
                    <th className="py-3 pr-4 text-right min-w-[100px]">Last Seen</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {rows.map(row => {
                    const debugLabel = extractDebugLabel(row.last_url);
                    const utmParams = extractUtmParams(row.last_url);
                    const ipParts = row.ip
                      ? row.ip
                          .split("|")
                          .map(part => part.trim())
                          .filter(Boolean)
                      : [];
                    const classifiedIps = ipParts.length ? classifyIpChain(ipParts) : [];

                    const rowKey = `${row.domain}-${row.last_seen}`;
                    const isExpanded = expandedRows.has(rowKey);

                    return (
                      <>
                        <tr key={rowKey} className="hover:bg-slate-50 transition-colors">
                          {/* Debug Toggle */}
                          <td className="py-3 pr-4">
                            <button
                              onClick={() => {
                                const newExpanded = new Set(expandedRows);
                                if (isExpanded) {
                                  newExpanded.delete(rowKey);
                                } else {
                                  newExpanded.add(rowKey);
                                }
                                setExpandedRows(newExpanded);
                              }}
                              className="text-slate-600 hover:text-slate-900 transition-colors"
                              title={isExpanded ? "Hide debug info" : "Show debug info"}
                            >
                              {isExpanded ? "▼" : "▶"}
                            </button>
                          </td>
                          {/* Company */}
                          <td className="py-3 pr-4">
                          <div className="font-medium text-slate-900 truncate max-w-[200px]">
                            {row.company_name || row.geo_org || row.name || "Unknown"}
                          </div>
                          {row.company_type && (
                            <span className={`inline-block mt-1 rounded px-1.5 py-0.5 text-[10px] font-semibold ${
                              row.company_type === 'business' ? 'bg-emerald-100 text-emerald-700' :
                              row.company_type === 'education' ? 'bg-blue-100 text-blue-700' :
                              'bg-slate-100 text-slate-600'
                            }`}>
                              {row.company_type.toUpperCase()}
                            </span>
                          )}
                          {row.enrichment_confidence && row.enrichment_confidence >= 70 && (
                            <span className="ml-1 text-xs text-emerald-600">✓</span>
                          )}
                        </td>
                        {/* Domain */}
                        <td className="py-3 pr-4">
                          <a
                            href={`https://${row.company_domain || row.domain}`}
                            target="_blank"
                            rel="noreferrer"
                            className="text-blue-600 hover:underline truncate block max-w-[160px]"
                          >
                            {row.company_domain || row.domain}
                          </a>
                          {row.company_domain && (
                            <span className="text-xs text-slate-400">✨</span>
                          )}
                        </td>
                        {/* UTM Parameters */}
                        <td className="py-3 pr-4">
                          {utmParams ? (
                            <div className="flex flex-wrap gap-1">
                              {utmParams.source && (
                                <span className="inline-block rounded-full bg-purple-100 px-2 py-0.5 text-[10px] font-medium text-purple-700">
                                  {utmParams.source}
                                </span>
                              )}
                              {utmParams.medium && (
                                <span className="inline-block rounded-full bg-blue-100 px-2 py-0.5 text-[10px] font-medium text-blue-700">
                                  {utmParams.medium}
                                </span>
                              )}
                              {utmParams.campaign && (
                                <span className="inline-block rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-medium text-emerald-700">
                                  {utmParams.campaign}
                                </span>
                              )}
                              {utmParams.term && (
                                <span className="inline-block rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-medium text-amber-700">
                                  {utmParams.term}
                                </span>
                              )}
                              {utmParams.content && (
                                <span className="inline-block rounded-full bg-pink-100 px-2 py-0.5 text-[10px] font-medium text-pink-700">
                                  {utmParams.content}
                                </span>
                              )}
                            </div>
                          ) : (
                            <span className="text-xs text-slate-400">—</span>
                          )}
                        </td>
                        {/* Device */}
                        <td className="py-3 pr-4">
                          <div className="text-sm space-y-0.5">
                            {row.browser && (
                              <div className="text-slate-700">
                                {row.browser === 'chrome' && '🟡'}{row.browser === 'firefox' && '🦊'}{row.browser === 'safari' && '🧭'}
                                <span className="ml-1">{row.browser.charAt(0).toUpperCase() + row.browser.slice(1)}</span>
                                {row.browser_version && <span className="text-xs text-slate-500"> {row.browser_version.split('.')[0]}</span>}
                              </div>
                            )}
                            {row.os && (
                              <div className="text-xs text-slate-500">
                                {row.os.charAt(0).toUpperCase() + row.os.slice(1)} {row.device_type && `• ${row.device_type}`}
                              </div>
                            )}
                          </div>
                        </td>
                        {/* Location */}
                        <td className="py-3 pr-4">
                          <div className="text-sm">
                            {row.geo_city && row.geo_country ? (
                              <>
                                <div className="font-medium text-slate-700">{row.geo_city}, {row.geo_country}</div>
                                {row.geo_region_name && <div className="text-xs text-slate-500">{row.geo_region_name}</div>}
                                {row.geo_isp && <div className="text-xs text-slate-400 truncate max-w-[180px]">{row.geo_isp}</div>}
                              </>
                            ) : (
                              <span className="text-slate-400">—</span>
                            )}
                          </div>
                        </td>
                        {/* Last URL */}
                        <td className="py-3 pr-4">
                          {row.last_url ? (
                            <a
                              href={row.last_url}
                              target="_blank"
                              rel="noreferrer"
                              className="text-xs text-blue-600 hover:underline truncate block max-w-[240px]"
                              title={row.last_url}
                            >
                              {row.last_url.replace(/^https?:\/\//, '')}
                            </a>
                          ) : (
                            <span className="text-xs text-slate-400">—</span>
                          )}
                        </td>
                        {/* Last Seen */}
                        <td className="py-3 pr-4 text-right">
                          <span className="text-sm text-slate-600">{formatRelative(row.last_seen)}</span>
                        </td>
                      </tr>
                      {/* Expandable Debug Row */}
                      {isExpanded && (
                        <tr key={`${rowKey}-debug`} className="bg-slate-50">
                          <td colSpan={8} className="py-4 px-4">
                            <div className="space-y-4 text-xs">
                              {/* Company & Enrichment */}
                              <div>
                                <h4 className="font-semibold text-slate-700 mb-2">Company & Enrichment</h4>
                                <div className="grid grid-cols-2 gap-2">
                                  <div><span className="text-slate-500">Company Name:</span> {row.company_name || "—"}</div>
                                  <div><span className="text-slate-500">Company Domain:</span> {row.company_domain || "—"}</div>
                                  <div><span className="text-slate-500">Company Type:</span> {row.company_type || "—"}</div>
                                  <div><span className="text-slate-500">Confidence:</span> {row.enrichment_confidence || row.confidence || "—"}</div>
                                  <div><span className="text-slate-500">Industry:</span> {row.industry || "—"}</div>
                                  <div><span className="text-slate-500">Size:</span> {row.size || "—"}</div>
                                </div>
                              </div>

                              {/* IP & Network */}
                              <div>
                                <h4 className="font-semibold text-slate-700 mb-2">IP & Network</h4>
                                <div className="grid grid-cols-2 gap-2">
                                  <div><span className="text-slate-500">IP:</span> {row.ip || "—"}</div>
                                  <div><span className="text-slate-500">ISP:</span> {row.geo_isp || "—"}</div>
                                  <div><span className="text-slate-500">Organization:</span> {row.geo_org || "—"}</div>
                                  <div><span className="text-slate-500">AS Number:</span> {row.geo_as || "—"}</div>
                                  <div><span className="text-slate-500">AS Name:</span> {row.geo_asname || "—"}</div>
                                  <div><span className="text-slate-500">Reverse DNS:</span> {row.geo_reverse_dns || "—"}</div>
                                  {row.diag_ipv4_source && <div><span className="text-slate-500">IPv4 Source:</span> <span className="font-mono text-[11px]">{row.diag_ipv4_source}</span></div>}
                                </div>
                              </div>

                              {/* Geolocation */}
                              <div>
                                <h4 className="font-semibold text-slate-700 mb-2">Geolocation</h4>
                                <div className="grid grid-cols-2 gap-2">
                                  <div><span className="text-slate-500">Country:</span> {row.geo_country} ({row.geo_country_code || "—"})</div>
                                  <div><span className="text-slate-500">Region:</span> {row.geo_region_name || "—"}</div>
                                  <div><span className="text-slate-500">City:</span> {row.geo_city || "—"}</div>
                                  <div><span className="text-slate-500">Zip:</span> {row.geo_zip || "—"}</div>
                                  <div><span className="text-slate-500">Coordinates:</span> {row.geo_lat && row.geo_lon ? `${row.geo_lat}, ${row.geo_lon}` : "—"}</div>
                                  <div><span className="text-slate-500">Timezone:</span> {row.geo_timezone || "—"}</div>
                                  <div><span className="text-slate-500">Currency:</span> {row.geo_currency || "—"}</div>
                                  <div><span className="text-slate-500">Mobile:</span> {row.geo_is_mobile ? "Yes" : "No"}</div>
                                  <div><span className="text-slate-500">VPN:</span> {row.geo_is_vpn ? "Yes" : "No"}</div>
                                  <div><span className="text-slate-500">Datacenter:</span> {row.geo_is_datacenter ? "Yes" : "No"}</div>
                                </div>
                              </div>

                              {/* Device & Browser */}
                              <div>
                                <h4 className="font-semibold text-slate-700 mb-2">Device & Browser</h4>
                                <div className="grid grid-cols-2 gap-2">
                                  <div><span className="text-slate-500">Browser:</span> {row.browser || "—"} {row.browser_version || ""}</div>
                                  <div><span className="text-slate-500">OS:</span> {row.os || "—"} {row.os_version || ""}</div>
                                  <div><span className="text-slate-500">Device Type:</span> {row.device_type || "—"}</div>
                                  <div><span className="text-slate-500">Device Vendor:</span> {row.device_vendor || "—"}</div>
                                  <div><span className="text-slate-500">Device Model:</span> {row.device_model || "—"}</div>
                                </div>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </>
                    );
                  })}
                </tbody>
              </table>
            )}
            {/* Pagination Controls */}
            {site && rows.length > 0 && (
              <div className="mt-4 flex items-center justify-between border-t border-slate-200 pt-4">
                <div className="text-sm text-slate-600">
                  Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, total)} of {total} companies
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    onClick={() => {
                      const newPage = currentPage - 1;
                      setCurrentPage(newPage);
                      void fetchFeed(site.site_id, { page: newPage });
                    }}
                    disabled={currentPage === 1 || state === "loading"}
                    variant="outline"
                    size="sm"
                  >
                    Previous
                  </Button>
                  <span className="text-sm text-slate-600">
                    Page {currentPage} of {Math.ceil(total / pageSize)}
                  </span>
                  <Button
                    onClick={() => {
                      const newPage = currentPage + 1;
                      setCurrentPage(newPage);
                      void fetchFeed(site.site_id, { page: newPage });
                    }}
                    disabled={currentPage >= Math.ceil(total / pageSize) || state === "loading"}
                    variant="outline"
                    size="sm"
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Visitor Map */}
        {site && (
          <Card>
            <CardHeader>
              <CardTitle>Visitor Map (All-Time)</CardTitle>
              <CardDescription>
                Interactive map showing all visitor locations with company info, timezone, and fraud detection.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <VisitorMap
                siteId={site.site_id}
                locations={mapLocations}
                newVisitorIps={newVisitorIps}
                onLocationClick={(location) => {
                  console.log('Clicked location:', location);
                }}
              />
            </CardContent>
          </Card>
        )}
          </>
        )}

        <Card>
          <CardHeader>
            <CardTitle>Embed snippet</CardTitle>
            <CardDescription>
              Drop this in your site header to start collecting anonymous company visits.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {site ? (
              <div className="space-y-3">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="flex items-center gap-2">
                    <span className="text-xs uppercase tracking-wider text-slate-500">Loader type:</span>
                    <div className="flex gap-1 rounded-md border border-slate-200 bg-slate-50 p-1">
                      <button
                        type="button"
                        onClick={() => setLoaderType("async")}
                        className={`rounded px-2 py-1 text-xs font-medium transition-colors ${
                          loaderType === "async"
                            ? "bg-white text-slate-900 shadow-sm"
                            : "text-slate-600 hover:text-slate-900"
                        }`}
                      >
                        Async
                      </button>
                      <button
                        type="button"
                        onClick={() => setLoaderType("sync")}
                        className={`rounded px-2 py-1 text-xs font-medium transition-colors ${
                          loaderType === "sync"
                            ? "bg-white text-slate-900 shadow-sm"
                            : "text-slate-600 hover:text-slate-900"
                        }`}
                      >
                        Sync
                      </button>
                    </div>
                  </div>
                  <Button type="button" variant="secondary" size="sm" onClick={handleCopySnippet}>
                    {copyState === "copied" ? "Copied!" : copyState === "error" ? "Copy failed" : "Copy snippet"}
                  </Button>
                </div>
                <pre className="max-h-60 overflow-auto rounded-md bg-slate-900 p-4 text-xs text-slate-100 ring-1 ring-slate-800">
                  <code className="whitespace-pre-wrap break-words">{snippet}</code>
                </pre>
                <div className="flex items-start justify-between gap-4">
                  <p className="text-xs text-slate-500">
                    {copyState === "copied"
                      ? "Snippet copied to clipboard."
                      : copyState === "error"
                        ? "Clipboard write failed. Try copying manually."
                        : loaderType === "async"
                          ? "Async loader: Non-blocking, prevents duplicate loading (recommended)."
                          : "Sync loader: Simple and compatible with all browsers."}
                  </p>
                </div>
              </div>
            ) : (
              <p className="text-sm text-slate-500">Generate a site ID to see your embed snippet.</p>
            )}
          </CardContent>
        </Card>

        {/* Danger Zone */}
        <Card className="border-red-200 bg-red-50/50">
          <CardHeader>
            <CardTitle className="text-red-700">⚠️ Danger Zone</CardTitle>
            <CardDescription className="text-red-600">
              Irreversible actions that will permanently delete data from the database.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="rounded-lg border border-red-200 bg-white p-4">
              <h3 className="font-semibold text-red-700 mb-2">Delete All Tracking Data</h3>
              <p className="text-sm text-slate-600 mb-3">
                Permanently delete all visitor tracking data including events, accounts, IP resolutions,
                and location clusters. Your site configuration will be preserved.
              </p>
              <PurgeDataButton />
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}

function PurgeDataButton() {
  const [showModal, setShowModal] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteResult, setDeleteResult] = useState<any>(null);

  const handlePurge = async () => {
    // Client-side UX check: require confirmation text to prevent accidental clicks
    // Real security validation (DELETE_CONFIRMATION_PHRASE) happens server-side only
    if (!confirmText) return;

    setIsDeleting(true);
    try {
      // Send both the confirmation token and the user-entered phrase to the server
      const params = new URLSearchParams({
        confirm: "yes-delete-everything",
        phrase: confirmText,
      });
      const response = await fetch(`/api/purge-all?${params.toString()}`, {
        method: "DELETE",
      });
      const result = await response.json();
      setDeleteResult(result);

      if (response.ok) {
        // Close modal and refresh after 2 seconds
        setTimeout(() => {
          setShowModal(false);
          setConfirmText("");
          setDeleteResult(null);
          window.location.reload();
        }, 2000);
      }
    } catch (error) {
      setDeleteResult({ error: "Failed to connect to server" });
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <>
      <Button
        variant="outline"
        className="border-red-300 text-red-700 hover:bg-red-100 hover:border-red-400"
        onClick={() => setShowModal(true)}
      >
        Delete All Data
      </Button>

      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
            <h2 className="text-xl font-bold text-red-700 mb-2">
              ⚠️ Delete All Tracking Data?
            </h2>
            <p className="text-sm text-slate-600 mb-4">
              This action <strong>cannot be undone</strong>. All visitor data will be permanently deleted
              from the database. Your site configuration will be preserved.
            </p>

            {!deleteResult ? (
              <>
                <div className="bg-red-50 border border-red-200 rounded p-3 mb-4">
                  <p className="text-sm text-red-700 font-medium mb-2">
                    🔒 Type the secret word to confirm deletion:
                  </p>
                  <p className="text-xs text-red-600 mb-2 italic">
                    ⚠️ Only the owner knows this word. Visitors cannot delete your valuable data.
                  </p>
                  <Input
                    type="text"
                    value={confirmText}
                    onChange={(e) => setConfirmText(e.target.value)}
                    placeholder="Enter secret word"
                    className="font-mono uppercase"
                    disabled={isDeleting}
                    autoComplete="off"
                    autoFocus
                  />
                </div>

                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setShowModal(false);
                      setConfirmText("");
                    }}
                    disabled={isDeleting}
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handlePurge}
                    disabled={!confirmText || isDeleting}
                    className="flex-1 bg-red-600 hover:bg-red-700 text-white disabled:opacity-50"
                  >
                    {isDeleting ? "Deleting..." : "🔥 Delete All Data"}
                  </Button>
                </div>
              </>
            ) : (
              <div className={`rounded p-3 ${deleteResult.error ? "bg-red-50 border border-red-200" : "bg-green-50 border border-green-200"}`}>
                {deleteResult.error ? (
                  <div className="space-y-3">
                    <p className="text-sm text-red-700">
                      ❌ Error: {deleteResult.error}
                    </p>
                    <Button
                      onClick={() => {
                        setDeleteResult(null);
                        setConfirmText("");
                      }}
                      className="w-full bg-slate-600 hover:bg-slate-700 text-white"
                    >
                      Try Again
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <p className="text-sm text-green-700 font-medium">
                      ✅ {deleteResult.message}
                    </p>
                    {deleteResult.stats && (
                      <div className="text-xs text-slate-600 space-y-0.5">
                        <div>Events: {deleteResult.stats.events_deleted}</div>
                        <div>Accounts: {deleteResult.stats.accounts_deleted}</div>
                        <div>IP Resolutions: {deleteResult.stats.ip_resolutions_deleted}</div>
                      </div>
                    )}
                    <p className="text-xs text-slate-500 mt-2">Refreshing page...</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}

function EmptyState({ state, error }: { state: FetchState; error?: string | null }) {
  if (state === "loading") {
    return <p className="py-10 text-center text-sm text-indigo-600">Loading recent visits…</p>;
  }
  if (state === "error") {
    return (
      <p className="py-10 text-center text-sm text-red-500" role="alert">
        {error ?? "Failed to load live feed. Try again."}
      </p>
    );
  }
  return <p className="py-10 text-center text-sm text-indigo-600">No visits captured yet. Install the tracking pixel to start collecting data.</p>;
}

function DiagnosticsPanel({ diagnostics, pixelMeta }: { diagnostics: Diagnostics; pixelMeta: LiveEventMeta | null }) {
  const generatedAt = new Date(diagnostics.timestamp).toLocaleString();
  const eventReceivedAt = new Date(diagnostics.event.received_at).toLocaleString();
  const dnsSummary = diagnostics.dnsCache
    ? `size ${diagnostics.dnsCache.size}, hits ${diagnostics.dnsCache.cacheHitsCompleted}, misses ${diagnostics.dnsCache.cacheMisses}`
    : "n/a";
  const dnsPrefetchNode = diagnostics.dnsPrefetch ? (
    <span className="font-mono break-all">
      {diagnostics.dnsPrefetch.hostname}:{diagnostics.dnsPrefetch.port}
      {" · "}
      {diagnostics.dnsPrefetch.supported
        ? diagnostics.dnsPrefetch.error
          ? `error: ${diagnostics.dnsPrefetch.error}`
          : diagnostics.dnsPrefetch.attempted
            ? "prefetched"
            : "not attempted"
        : "unsupported"}
    </span>
  ) : (
    "n/a"
  );

  // Classify and format IP addresses
  const classifiedIps = diagnostics.client.ipChain?.length
    ? classifyIpChain(diagnostics.client.ipChain)
    : [];

  const ipChainDisplay = classifiedIps.length > 0 ? (
    <div className="space-y-1">
      {classifiedIps.map((ip, idx) => (
        <div key={idx} className="font-mono text-xs">
          <span className="inline-block min-w-[40px] rounded bg-slate-200 px-1.5 py-0.5 text-[10px] font-semibold text-slate-600">
            {ip.label}
          </span>
          <span className="ml-2">{ip.address}</span>
          {idx === 0 && (
            <span className="ml-2 text-[10px] text-blue-600 font-semibold">• Client IP</span>
          )}
        </div>
      ))}
    </div>
  ) : (
    <span className="text-slate-400">—</span>
  );

  return (
    <div className="mt-3 rounded-md border border-slate-200 bg-slate-50 p-3">
      <p className="text-[11px] font-semibold uppercase tracking-[0.15em] text-slate-500">Runtime diagnostics</p>
      <dl className="mt-2 grid gap-x-6 gap-y-3 text-xs text-slate-700 sm:grid-cols-2">
        <DiagnosticsRow label="Generated" value={generatedAt} />
        <DiagnosticsRow label="Environment" value={diagnostics.environment} />
        <DiagnosticsRow
          label="Bun"
          value={
            <span className="font-mono">
              {diagnostics.bun.version}
              {diagnostics.bun.revision ? ` (${diagnostics.bun.revision})` : ""}
              {diagnostics.bun.runtime ? ` · ${diagnostics.bun.runtime}` : ""}
            </span>
          }
        />
        <DiagnosticsRow label="Server" value={<span className="font-mono break-all">{diagnostics.server.url}</span>} />
        <DiagnosticsRow label="Database" value={<span className="font-mono break-all">{diagnostics.database.path}</span>} />
        <DiagnosticsRow label="DNS cache" value={dnsSummary} />
        <DiagnosticsRow label="DNS prefetch" value={dnsPrefetchNode} />
        <DiagnosticsRow label="Site URL" value={<span className="font-mono break-all">{diagnostics.site.url}</span>} />
        <DiagnosticsRow
          label="Event"
          value={<span className="font-mono break-all">{`${diagnostics.event.type} · ${diagnostics.event.id}`}</span>}
        />
        <DiagnosticsRow label="Event received" value={eventReceivedAt} />
        {diagnostics.client.ipv4 && (
          <DiagnosticsRow
            label="Public IPv4"
            value={
              <div className="font-mono text-xs">
                <span className="inline-block rounded bg-emerald-100 px-2 py-1 font-semibold text-emerald-700">
                  {diagnostics.client.ipv4}
                </span>
                <span className="ml-2 text-[10px] text-slate-500">• From ipify.org</span>
              </div>
            }
          />
        )}
        <DiagnosticsRow label="Client IP addresses" value={ipChainDisplay} />
        <DiagnosticsRow
          label="User agent"
          value={
            <code className="block max-h-24 overflow-auto break-words text-[11px]">{diagnostics.client.userAgent ?? "—"}</code>
          }
        />
      </dl>

      {/* Pixel Diagnostics */}
      {pixelMeta && (
        <>
          {/* IPv4 Diagnostics */}
          {pixelMeta.ipv4_diagnostics && (
            <>
              <p className="mt-6 text-[11px] font-semibold uppercase tracking-[0.15em] text-slate-500">IPv4 Diagnostics</p>
              <dl className="mt-2 grid gap-x-6 gap-y-3 text-xs text-slate-700 sm:grid-cols-2">
                <DiagnosticsRow
                  label="IPv4 Resolved"
                  value={
                    pixelMeta.ipv4_diagnostics.ipv4_resolved ? (
                      <span className="inline-flex items-center gap-1">
                        <span className="inline-block h-2 w-2 rounded-full bg-emerald-500" />
                        <span className="font-semibold text-emerald-700">Success</span>
                        {pixelMeta.ipv4_diagnostics.ipv4_duration_ms && (
                          <span className="text-slate-500">({pixelMeta.ipv4_diagnostics.ipv4_duration_ms}ms)</span>
                        )}
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1">
                        <span className="inline-block h-2 w-2 rounded-full bg-red-500" />
                        <span className="font-semibold text-red-700">Failed</span>
                      </span>
                    )
                  }
                />
                {pixelMeta.ipv4_diagnostics.ipv4_source && (
                  <DiagnosticsRow label="IPv4 Source" value={<span className="font-mono text-[11px]">{pixelMeta.ipv4_diagnostics.ipv4_source}</span>} />
                )}
                {pixelMeta.ipv4_diagnostics.ipv4_error && (
                  <DiagnosticsRow label="IPv4 Error" value={<span className="text-red-600">{pixelMeta.ipv4_diagnostics.ipv4_error}</span>} />
                )}
              </dl>
            </>
          )}

        </>
      )}
    </div>
  );
}

function DiagnosticsRow({ label, value, className }: { label: string; value: ReactNode; className?: string }) {
  const valueClasses = className ? `mt-0.5 text-xs text-slate-700 ${className}` : "mt-0.5 text-xs text-slate-700";
  return (
    <div>
      <dt className="text-[11px] uppercase tracking-[0.15em] text-slate-400">{label}</dt>
      <dd className={valueClasses}>{value}</dd>
    </div>
  );
}

function isDiagnostics(value: unknown): value is Diagnostics {
  if (!value || typeof value !== "object") return false;
  return "timestamp" in value && "bun" in value && "server" in value;
}

function formatUrl(url: string, options: { includeQuery?: boolean; includeProtocol?: boolean } = {}) {
  try {
    const parsed = new URL(url);
    const prefix = options.includeProtocol ? `${parsed.protocol}//${parsed.host}` : parsed.hostname;
    const path = parsed.pathname || "/";
    if (options.includeQuery && parsed.search) {
      return `${prefix}${path}${parsed.search}`;
    }
    return `${prefix}${path}` + (parsed.search ? "…" : "");
  } catch {
    return url;
  }
}

function extractDebugLabel(url: string | null): string | null {
  if (!url) return null;
  try {
    const params = new URL(url).searchParams;
    for (const key of ["tester", "debug", "tag", "label"]) {
      const value = params.get(key);
      if (value) {
        return `${key}=${value}`;
      }
    }
    return null;
  } catch {
    return null;
  }
}

function extractUtmParams(url: string | null): { source?: string; medium?: string; campaign?: string; term?: string; content?: string } | null {
  if (!url) return null;
  try {
    const params = new URL(url).searchParams;
    const utmParams: any = {};

    const source = params.get("utm_source");
    const medium = params.get("utm_medium");
    const campaign = params.get("utm_campaign");
    const term = params.get("utm_term");
    const content = params.get("utm_content");

    if (source) utmParams.source = source;
    if (medium) utmParams.medium = medium;
    if (campaign) utmParams.campaign = campaign;
    if (term) utmParams.term = term;
    if (content) utmParams.content = content;

    return Object.keys(utmParams).length > 0 ? utmParams : null;
  } catch {
    return null;
  }
}

function extractSubpath(url: string | null): string {
  if (!url) return "/";
  try {
    const urlObj = new URL(url);
    return urlObj.pathname || "/";
  } catch {
    return "/";
  }
}

function formatDeleteError(code: string): string {
  switch (code) {
    case "missing_parameters":
    case "invalid_json":
      return "Invalid delete request. Refresh and try again.";
    case "unknown_error":
      return "Server error while deleting company data.";
    default:
      return "Failed to delete company data. Try again.";
  }
}

function formatRelative(timestamp: number) {
  const delta = Date.now() - timestamp;
  if (Number.isNaN(delta) || delta < 0) return "just now";
  const minutes = Math.floor(delta / 60000);
  if (minutes < 1) return "just now";
  if (minutes === 1) return "1 minute ago";
  if (minutes < 60) return `${minutes} minutes ago`;
  const hours = Math.floor(minutes / 60);
  if (hours === 1) return "1 hour ago";
  if (hours < 24) return `${hours} hours ago`;
  const days = Math.floor(hours / 24);
  return days === 1 ? "1 day ago" : `${days} days ago`;
}

function formatSiteError(code: string): string {
  switch (code) {
    case "invalid_site_scheme":
      return "Enter a URL that starts with http:// or https://.";
    case "invalid_site_url":
      return "Enter a valid website URL.";
    case "invalid_site_id":
      return "Custom site IDs can only include letters, numbers, underscores, or dashes.";
    case "failed_to_create_site":
      return "Could not create a site with that ID. Try a different value.";
    case "missing_url":
      return "Please enter your website URL.";
    case "unknown_error":
      return "Something went wrong while generating the site ID. Try again.";
    default:
      return code;
  }
}

export default App;
